import { useEffect, useRef } from 'react';

// How long the tab must have been hidden before returning to it is worth a refetch.
// A quick alt-tab is not staleness, and refetching on every flip would put a burst of
// requests on the backend for nothing.
const DEFAULT_MIN_HIDDEN_MS = 30_000;

/**
 * Refetch when the user comes back to a tab that has been sitting idle.
 *
 * Every page in this app loads on mount and never again, so a tab left open shows a
 * snapshot from whenever it was last navigated to — records added since, by another
 * device or another user, stay invisible until a manual reload. Timers do not close
 * the gap: browsers throttle intervals in hidden tabs and freeze them outright on
 * mobile, which is why the receipt poller's 60s interval was observed firing nine
 * minutes apart.
 *
 * `callback` is held in a ref, so it does not need to be referentially stable — pages
 * whose loader is a plain async function can adopt this without being rewritten to
 * useCallback.
 */
export default function useRefreshOnFocus(callback, { minHiddenMs = DEFAULT_MIN_HIDDEN_MS } = {}) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const hiddenAtRef = useRef(null);

  useEffect(() => {
    const refresh = () => {
      const fn = callbackRef.current;
      if (typeof fn === 'function') fn();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt !== null && Date.now() - hiddenAt >= minHiddenMs) {
        refresh();
      }
    };

    // Mobile Safari restores a back-navigation from its bfcache without reliably
    // firing visibilitychange, so the page would otherwise come back showing the
    // state it was frozen in.
    const handlePageShow = (event) => {
      if (event.persisted) {
        hiddenAtRef.current = null;
        refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [minHiddenMs]);
}
